import React from 'react';
import { Box, ListSubheader, Typography, type SxProps, type Theme } from '@mui/material';

interface SearchResultColumnProps {
  /** Section icon shown before the label, e.g. `<PatientIcon sx={{ fontSize: 16 }} />`. */
  icon: React.ReactNode;
  /** Section label — rendered as "{label} · {count}". */
  label: string;
  count: number;
  /**
   * Shown in place of the rows when `count` is 0, so the column still
   * renders its header and the grid doesn't read as broken when only one
   * side has results (PRX-57, ruling R5).
   */
  emptyText: string;
  /** Extra inline content after the count, e.g. the Kroll-fallback suffix on the Patients header. */
  extra?: React.ReactNode;
  /** The rows themselves (typically a `<List>` of `ListItemButton`s) — rendered only when `count > 0`. */
  children: React.ReactNode;
  'data-testid'?: string;
  /**
   * Extra styles merged onto the root Box, on top of `{ minWidth: 0 }`.
   * The Patients column (a top-level scroll region on its own) passes
   * `{ height: '100%', overflowY: 'auto' }` here; a column nested inside a
   * shared scrolling wrapper (Orders, Subscriptions) leaves this unset so
   * it scrolls with its wrapper instead of independently (PRX-57 review).
   */
  sx?: SxProps<Theme>;
}

/**
 * PRX-57: header + count + empty-state + rows for one column of the global
 * search results grid (`GlobalSearchPalette.tsx`). Used for the Patients
 * column and, stacked twice, for the Orders/Subscriptions right column —
 * split out so those column bodies stay readable in the palette file.
 */
export function SearchResultColumn({
  icon,
  label,
  count,
  emptyText,
  extra,
  children,
  'data-testid': dataTestId,
  sx,
}: SearchResultColumnProps) {
  return (
    <Box data-testid={dataTestId} sx={[{ minWidth: 0 }, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])]}>
      <ListSubheader
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          lineHeight: '34px',
          fontWeight: 700,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          color: 'text.secondary',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {icon}
          {label} · {count}
          {extra}
        </Box>
      </ListSubheader>
      {count > 0 ? (
        children
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 1.5 }}>
          {emptyText}
        </Typography>
      )}
    </Box>
  );
}
